import { createRoute } from "@tanstack/solid-router";

import { rootRoute } from "../__root";
import { sessionActor } from "@/lib/session-machine";
import { accountsStore, readAccountsStateSync, readActiveAccountSync } from "@/lib/accounts";
import { api, fetchApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import {
  cloneSession,
  type components,
  HTTP_FORBIDDEN,
  HTTP_UNAUTHORIZED,
  isErrorModel,
  isStatusError,
  isValidSession,
  queryKeys,
  type Session,
} from "@folio/shared";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { AppLayoutContent } from "@/features/layout/components/app-layout-content";
import { SESSION_CHECK_TIMEOUT_MS, SESSION_STALE_TIME_MS } from "@/lib/constants";

function AppLayout() {
  const { db } = usePowerSyncContext();

  // Mount the shell immediately — do NOT gate on `db()`. While PowerSync's
  // local DB initializes, AppLayoutContent renders the static chrome and
  // shows granular per-region skeletons (db is passed possibly-null).
  return <AppLayoutContent db={db()} />;
}

// Deduplicates concurrent beforeLoad calls during the page-load preload burst.
// When viewport preloads fire simultaneously for multiple child routes, they all
// share a single API request instead of each sending their own.
let activeSessionCheck: Promise<void> | null = null;

async function runSessionCheck(
  snapshot: ReturnType<typeof sessionActor.getSnapshot>,
): Promise<void> {
  // Wait for the machine to finish priming from storage.
  if (snapshot.matches("priming")) {
    await new Promise<void>((resolve) => {
      const sub = sessionActor.subscribe((s) => {
        if (!s.matches("priming")) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  if (readActiveAccountSync()?.kind === "guest") {
    return;
  }

  const currentSnapshot = sessionActor.getSnapshot();
  // Machine may have recovered a session from localStorage during priming.
  // If we have a session OR we have already verified with the server, we skip.
  if (currentSnapshot.context.session || currentSnapshot.context.isVerified) {
    return;
  }

  // No session in machine — verify with the server once.
  const fetchSession = async () =>
    api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
  type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

  const sessionResult = await (async () => {
    try {
      const timeoutPromise = new Promise<FetchResult>((_resolve, reject) => {
        setTimeout(() => {
          reject(new Error("Session check timeout"));
        }, SESSION_CHECK_TIMEOUT_MS);
      });

      return await Promise.race([
        api.GET("/api/auth/get-session", {
          params: { query: { jwt: true } },
        }),
        timeoutPromise,
      ]);
    } catch (err) {
      console.warn("[SessionCheck] Error or timeout checking session:", err);
      return null;
    }
  })();

  if (!sessionResult) {
    return;
  }

  const { data, error, response } = sessionResult;

  // Sync with machine regardless of success/error (error will clear session)
  let sessionPayload: Session | null = null;
  if (isValidSession(data)) {
    sessionPayload = cloneSession(data);
  }

  let errorPayload: components["schemas"]["ErrorModel"] | null = null;
  if (isErrorModel(error)) {
    errorPayload = error;
  }

  sessionActor.send({
    type: "SERVER_REPLY",
    session: sessionPayload,
    error: errorPayload,
  });

  if (error) {
    const status = isStatusError(error) ? error.status : response.status;
    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
      return;
    }
    return;
  }

  queryClient.setQueryData(queryKeys.auth.session(), data);
}

// Addressable global overlays, defined on `_app` layout so that
// ANY child route inherits them:
export interface AppLayoutSearch {
  palette?: boolean;
  q?: string;
  scope?: "page";
  modal?: "shortcuts";
  account?: "switch";
  rename?: string;
}

const validateAppLayoutSearch = (search: Record<string, unknown>): AppLayoutSearch => ({
  palette:
    search.palette === true ||
    search.palette === 1 ||
    search.palette === "1" ||
    search.palette === "true" ||
    undefined,
  q: typeof search.q === "string" && search.q.trim() !== "" ? search.q : undefined,
  scope: search.scope === "page" ? "page" : undefined,
  modal: search.modal === "shortcuts" ? "shortcuts" : undefined,
  account: search.account === "switch" ? "switch" : undefined,
  rename:
    typeof search.rename === "string" && search.rename.trim() !== "" ? search.rename : undefined,
});

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  validateSearch: validateAppLayoutSearch,
  beforeLoad: () => {
    const accountsState = readAccountsStateSync();
    if (accountsState.accounts.length === 0) {
      void accountsStore.ensureGuestActive();
    }

    const snapshot = sessionActor.getSnapshot();

    if (!snapshot.matches("priming") && snapshot.context.session) {
      const cached = queryClient.getQueryState(queryKeys.auth.session());
      const isStale =
        !cached?.dataUpdatedAt || Date.now() - cached.dataUpdatedAt > SESSION_STALE_TIME_MS;

      if (isStale) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.auth.session(),
          queryFn: async () =>
            fetchApi(api.GET("/api/auth/get-session", { params: { query: { jwt: true } } })),
          staleTime: SESSION_STALE_TIME_MS,
        });
      }
      return;
    }

    activeSessionCheck ??= (async () => {
      try {
        await runSessionCheck(snapshot);
      } finally {
        activeSessionCheck = null;
      }
    })();
  },

  component: AppLayout,
});
